import { addToast, Button, TextAreaInput } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import AzureFilesUploader from '../../../components/AzureFilesUploader/AzureFilesUploader';
import {
  FileRequiredTypes,
  SecureResponseFile,
} from '../../../components/AzureFilesUploader/types';
import ValidationFileChip from '../../../components/AzureFilesUploader/ValidationFileChip';
import IconOption from '../../../components/SelectedWithIcon/IconOption';
import { SelectControlled } from '../../../components/SelectedWithIcon/SelectedWithIcon';
import { TranslationsKeys } from '../../../locales/translations';
import { storeTeacherJustification, updateTeacherJustification } from '../api';
import {
  optionsSelectLessonTeacher,
  statusColorMapLessonTeacher,
  statusIconMapLessonTeacher,
  TeacherAttendanceFile,
} from '../types';
import { convertFieldsToSubmit } from '../utils';
import CreatedAt from './components/CreatedAt';
import CreatedBy from './components/CreatedBy';
import { useTeacherAttendance } from './TeacherAttendanceLoader';
// Como viene del form
export type ScheduleSectionFormData = {};

type CreateTeacherJustificationFormProps = {
  defaultValues?: any;
  rowJustification: boolean;
  lessonId: number;
};

const CreateTeacherJustificationForm = ({
  defaultValues,
  rowJustification,
  lessonId,
}: CreateTeacherJustificationFormProps) => {
  const { t } = useTranslation(TranslationsKeys.TEACHER_ATTENDANCE_LIST);
  const action = rowJustification ? 'Update' : 'Create';
  const texts = useMemo(
    () => ({
      form: {
        justificationDescriptionLabel: t(`form.justificationDescriptionLabel`),
        observationDescriptionLabel: t(`form.observationDescriptionLabel`),
        complementaryFileLabel: t(`form.complementaryFileLabel`),
        justificationStatusLabel: t(`form.justificationStatusLabel`),
        createdByLabel: t(`form.createdByLabel`),
        createdByTooltip: (name: string) =>
          t(`form.createdByTooltip`, { name }),
        status: {
          Pending: t(`form.status.pending`),
          Approved: t(`form.status.approved`),
          Rejected: t(`form.status.rejected`),
        },
        successOnCreate: t(`form.successOnCreate`),
        errorOnCreate: t(`form.errorOnCreate`),
        successOnUpdate: t(`form.successOnUpdate`),
        errorOnUpdate: t(`form.errorOnUpdate`),
        actions: {
          save: t(`form.actions.save`),
          update: t(`form.actions.update`),
        },
        msgValidations: {
          required: t(`common.validations.required`),
        },
      },
      actions: {
        uploadFile: t(`upload.uploadAction`),
      },
      download: {
        label: t(`form.downloadComplementaryFileLabel`),
        button: t(`downloads.downloadAction`),
        failed: t(`downloads.downloadFailed`),
        success: t(`downloads.downloadSuccess`),
      },
      upload: {
        label: t(`form.uploadComplementaryFileOptionalLabel`),
        button: t(`upload.uploadAction`),
        failed: t(`upload.uploadFailed`),
        success: t(`upload.uploadSuccess`),
      },
    }),
    [t],
  );
  const { refresh } = useTeacherAttendance();

  const methods = useForm({
    defaultValues,
    mode: 'onSubmit',
  });
  const { handleSubmit, control, watch, formState } = methods;

  const [
    files,
    adminCreatedAt,
    adminObservation,
    teacherName,
    teacherCreatedAt,
    status,
  ] = watch([
    `complementaryFilesIds_${lessonId}`,
    `observationCreatedAt_${lessonId}`,
    `observationDescription_${lessonId}`,
    `justificationCreatedBy_${lessonId}`,
    `justificationCreatedAt_${lessonId}`,
    `status_${lessonId}`,
  ]);

  const complementaryFiles = useFieldArray({
    control,
    name: `complementaryFilesIds_${lessonId}`,
  });
  const [filesState, setFilesState] = useState(0);
  const handleAddFile = useCallback(
    (file: SecureResponseFile) => {
      complementaryFiles.append(file);
      setFilesState((prev) => prev + 1);
    },
    [complementaryFiles],
  );

  const handleDownloadFile = useCallback((file: SecureResponseFile) => {
    if (!file?.url) {
      return;
    }
    window.open(file?.url, '_blank');
  }, []);

  const handleRemoveFile = useCallback(
    (index: number) => {
      complementaryFiles.remove(index);
      setFilesState((prev) => prev + 1);
    },
    [complementaryFiles],
  );

  const onSubmit = useCallback(
    async (values: ScheduleSectionFormData) => {
      if (action === 'Update') {
        const { data, error } = await updateTeacherJustification(
          convertFieldsToSubmit(lessonId, values),
        );
        if (error) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: texts.form.errorOnUpdate,
          });
        }
        if (data) {
          addToast({
            icon: 'check',
            color: 'success',
            text: texts.form.successOnUpdate,
          });
          refresh();
        }
        return;
      }

      const { data, error } = await storeTeacherJustification(
        convertFieldsToSubmit(lessonId, values),
      );
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.form.errorOnCreate,
        });
      }
      if (data) {
        addToast({
          icon: 'check',
          color: 'success',
          text: texts.form.successOnCreate,
        });
        refresh();
      }
    },
    [lessonId, refresh, action, texts],
  );

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12} className="mb-3">
            <Row>
              <Col xs={12} lg={6}>
                <Row>
                  <Col className="d-flex align-items-center justify-content-between">
                    <label className="g-file-input-label text-muted">
                      {texts.form.justificationDescriptionLabel}
                    </label>
                    {rowJustification && <CreatedAt date={teacherCreatedAt} />}
                  </Col>
                </Row>
                <TextAreaInput
                  className="mb-3"
                  key={`justificationDescription_${lessonId}`}
                  name={`justificationDescription_${lessonId}`}
                  label=""
                  placeholder=""
                  rules={{ required: texts.form.msgValidations.required }}
                  control={control}
                  maxLength={255}
                />
                {rowJustification && (
                  <CreatedBy
                    lessonId={lessonId}
                    name={teacherName}
                    texts={{
                      tooltip: texts.form.createdByTooltip,
                    }}
                  />
                )}
              </Col>
              <Col xs={12} lg={6}>
                <Row>
                  <Col className="d-flex align-items-center justify-content-between">
                    <label className="g-file-input-label text-muted">
                      {texts.form.observationDescriptionLabel}
                    </label>
                    {!!adminCreatedAt && <CreatedAt date={adminCreatedAt} />}
                  </Col>
                </Row>
                <TextAreaInput
                  key={`observationDescription_${lessonId}`}
                  name={`observationDescription_${lessonId}`}
                  label=""
                  placeholder={!adminObservation ? '-No registrada-' : ''}
                  control={control}
                  disabled={true}
                  maxLength={255}
                />
              </Col>
            </Row>
          </Col>
          <Col xs={12}>
            <Row className={`d-flex ${!!status ? '' : 'align-items-end'} `}>
              <Col xs={12} lg={6}>
                <div key={`${lessonId}_${filesState.toString()}`}>
                  <AzureFilesUploader
                    fileType={FileRequiredTypes.JustificationLessonTeacher}
                    label={texts.form.complementaryFileLabel}
                    disabled={false}
                    onUploaded={(next) => handleAddFile(next)}
                  />
                  {!!files?.length && (
                    <div
                      className="w-full mt-3 d-flex flex-wrap align-items-center justify-content-start"
                      style={{ gap: 5 }}
                    >
                      {files?.map((file: TeacherAttendanceFile, i: number) => (
                        <ValidationFileChip
                          key={file.id.toString()}
                          value={i}
                          children={`${file.name}.${file.extension}`}
                          onRemove={
                            !file?.isFromAdmin ? handleRemoveFile : undefined
                          }
                          onDownload={() => handleDownloadFile(file)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </Col>
              <Col xs={12} lg={6}>
                <Row>
                  <Col md={7}>
                    {!!status && (
                      <>
                        <label
                          className="g-file-input-label text-muted"
                          htmlFor={`status_${lessonId}`}
                        >
                          {texts.form.justificationStatusLabel}
                        </label>
                        <SelectControlled
                          name={`status_${lessonId}`}
                          control={control}
                          options={optionsSelectLessonTeacher}
                          components={{
                            SingleValue: (props: any) => (
                              <IconOption
                                {...props}
                                statusColorMap={statusColorMapLessonTeacher}
                                statusIconMap={statusIconMapLessonTeacher}
                              />
                            ),
                          }}
                          onlyView={true}
                          disabled={true}
                        />
                      </>
                    )}
                  </Col>
                  <Col
                    md={5}
                    className={`d-flex align-items-end ${
                      !!status ? '' : 'mb-2'
                    }`}
                  >
                    <Button
                      size="md"
                      fullwidth
                      text={
                        action === 'Update'
                          ? texts.form.actions.update
                          : texts.form.actions.save
                      }
                      type="submit"
                      loading={formState.isSubmitting}
                      disabled={formState.isSubmitting}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};

export default CreateTeacherJustificationForm;
